import { addDays, formatISO, parseISO, startOfToday } from 'date-fns';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackApplyDatesModal, trackCloseDatesModal, trackOpenDatesModal, trackResetDatesModal } from './tracking';
import { useFiltersContext } from '../../../../packages/Filters/shared/utils/useFiltersContext';
import { getDateRangeValue, isJsonQueryDatesSimilar, TJsonQuery } from '../../../../packages/JsonQuery';
import { IJsonQueryDateRangeValue } from '../../../../packages/api-models/common/json_query';
import { closeDatesModal, openDatesModal } from '../../../common/actions/filters/datesModal';
import { LOCAL_STORAGE_IS_MOBILE_FILTERS_WERE_APPLIED } from '../../../common/hooks/useUxFbPoll/constants';
import {
  getLocalStorageFiltersWereAppliedTwoTimes,
  setLocalStorageFiltersApplied,
} from '../../../common/hooks/useUxFbPoll/localStorageUtils';
import { selectDatesModalState } from '../../../common/selectors/filters/datesModal/selectDatesModalState';
import { TThunkDispatch } from '../../../common/types/redux';
import { formatDateRage } from '../../../common/utils/formatDateRage';
import { DatesChipContent } from '../../components/DatesChipContent';

interface IState {
  jsonQueryDates: IJsonQueryDateRangeValue | null;
  fromValue: Date | undefined;
  toValue: Date | undefined;
}

const initialState: IState = {
  jsonQueryDates: null,
  fromValue: undefined,
  toValue: undefined,
};

interface IJsonQueryDatesChanged {
  type: 'jsonQueryDatesChanged';
  jsonQueryDates: IJsonQueryDateRangeValue | null;
}

interface IRangeChanged {
  type: 'rangeChanged';
  fromValue: Date | undefined;
  toValue: Date | undefined;
}

type TActions = IJsonQueryDatesChanged | IRangeChanged;

function reducer(state: IState, action: TActions): IState {
  switch (action.type) {
    case 'rangeChanged':
      return { ...state, fromValue: action.fromValue, toValue: action.toValue };

    case 'jsonQueryDatesChanged':
      return {
        jsonQueryDates: action.jsonQueryDates,
        fromValue: action.jsonQueryDates ? parseISO(action.jsonQueryDates.gte) : undefined,
        toValue: action.jsonQueryDates ? parseISO(action.jsonQueryDates.lt) : undefined,
      };
  }
}

const getChipLabel = (innerReducerState: IState, jsonQuery: TJsonQuery) => {
  const dateFrom = jsonQuery.dates ? new Date(jsonQuery.dates.value.gte) : innerReducerState.fromValue;
  const dateTo = jsonQuery.dates ? new Date(jsonQuery.dates.value.lt) : innerReducerState.toValue;

  return formatDateRage(dateFrom, dateTo, true);
};

export const DatesChipContainer = () => {
  const { jsonQuery, onChange, onApply, onApplyClick } = useFiltersContext();
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const reduxDispatch = useDispatch<TThunkDispatch>();
  const { isOpen } = useSelector(selectDatesModalState);

  const today = startOfToday();
  const minDate = today;
  const maxDate = addDays(today, 365);

  const isLocalStorageFiltersWereAppliedTwoTimes = getLocalStorageFiltersWereAppliedTwoTimes(
    LOCAL_STORAGE_IS_MOBILE_FILTERS_WERE_APPLIED,
  );

  const handleChange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      let fromValue = from;
      let toValue = to;

      if (fromValue && !toValue) {
        toValue = addDays(fromValue, 1);
      }
      if (toValue && !fromValue) {
        fromValue = addDays(toValue, -1);
      }
      if (fromValue && toValue && fromValue >= toValue) {
        toValue = addDays(fromValue, 1);
      }

      dispatch({
        type: 'rangeChanged',
        fromValue,
        toValue,
      });

      if (fromValue && toValue) {
        const gte = formatISO(fromValue, { representation: 'date' });
        const lt = formatISO(toValue, { representation: 'date' });

        if (!state.jsonQueryDates || !isJsonQueryDatesSimilar(state.jsonQueryDates, { gte, lt })) {
          onChange({ action: 'setDates', arguments: [gte, lt] });
        }
      } else if (state.jsonQueryDates) {
        onChange({ action: 'resetTerms', arguments: [['dates']] });
      }
    },
    [state, onChange],
  );

  React.useEffect(() => {
    const jsonQueryDates = getDateRangeValue('dates')(jsonQuery);

    if (!isJsonQueryDatesSimilar(state.jsonQueryDates, jsonQueryDates)) {
      dispatch({
        type: 'jsonQueryDatesChanged',
        jsonQueryDates,
      });
    }
  }, [state, jsonQuery]);

  React.useEffect(() => {
    const isFromGreaterThanTo = state.fromValue && state.toValue && state.fromValue >= state.toValue;

    if (isFromGreaterThanTo) {
      onChange({ action: 'resetTerms', arguments: [['dates']] });
    }
  }, [state, onChange]);

  const handleClick = React.useCallback(() => {
    trackOpenDatesModal();
    reduxDispatch(openDatesModal());
  }, []);

  const handleApply = React.useCallback(() => {
    trackApplyDatesModal();
    onApply();
    if (onApplyClick) {
      onApplyClick();
    }

    reduxDispatch(closeDatesModal());

    //Если фильтр был применен 2 раза, сохраняем значеине в LocalStorage
    if (!isLocalStorageFiltersWereAppliedTwoTimes) {
      setLocalStorageFiltersApplied(LOCAL_STORAGE_IS_MOBILE_FILTERS_WERE_APPLIED);
    }
  }, [onApply, onApplyClick, isLocalStorageFiltersWereAppliedTwoTimes, setLocalStorageFiltersApplied]);

  const handleClose = React.useCallback(() => {
    trackCloseDatesModal();
    reduxDispatch(closeDatesModal());
  }, []);

  const handleReset = React.useCallback(() => {
    trackResetDatesModal();
  }, []);

  const label = getChipLabel(state, jsonQuery);

  return (
    <DatesChipContent
      isOpen={isOpen}
      label={label}
      fromValue={state.fromValue}
      toValue={state.toValue}
      minDate={minDate}
      maxDate={maxDate}
      handleClose={handleClose}
      handleClick={handleClick}
      handleChange={handleChange}
      handleApply={handleApply}
      handleReset={handleReset}
    />
  );
};
